'use client';

import { useState, useRef } from 'react';

import { Button } from '@/components/button';
import { cn } from '@/utils/cn';

import { REFERRAL_CODE_ERRORS, type ReferralCodeError } from '../validation.utils';

import Icons from './Icons';
import InputErrorMessage from './InputErrorMessage';

interface CopyReferralProps {
  validationError?: ReferralCodeError | null;
  isReferralCheckError?: boolean;
  title: string;
  value: string;
  onChange?: (value: string) => void;
}

const CopyReferral: React.FC<CopyReferralProps> = ({
  validationError = null,
  isReferralCheckError = false,
  title,
  value,
  onChange,
}: CopyReferralProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);

      // reset copied state after 1 sec
      setTimeout(() => {
        setIsCopied(false);
      }, 1000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <Icons.Referral />
          <p className='text-lg text-text-secondary leading-[0.36px]'>{title}</p>
        </div>
      </div>
      <div className='flex flex-col gap-1'>
        <div className='relative'>
          <input
            ref={inputRef}
            type='text'
            value={value}
            readOnly={!onChange}
            className={cn(
              'flex items-center gap-4 justify-between w-full pl-4 pr-12 h-[46px] bg-surface-secondary rounded-lg text-text-primary leading-6 truncate focus:outline-none focus:ring-1 focus:ring-border-secondary',
              (!!validationError || isReferralCheckError) && 'ring-1 ring-red-100 focus:ring-1 focus:ring-red-100',
            )}
            onChange={handleChange}
          />
          <Button
            variant='tertiary'
            disabled={isCopied}
            onClick={handleCopy}
            className={cn('absolute right-1 top-1/2 -translate-y-1/2 bg-transparent transition-all duration-300', isCopied && 'scale-110')}
          >
            <div className='w-6 h-6 flex items-center justify-center'>
              {isCopied ? <Icons.Check className='text-green-100' width={24} height={24} /> : <Icons.Copy />}
            </div>
          </Button>
        </div>
        {isReferralCheckError && <InputErrorMessage message={REFERRAL_CODE_ERRORS.RESERVED} />}
        {!!validationError && <InputErrorMessage message={REFERRAL_CODE_ERRORS[validationError]} />}
      </div>
    </div>
  );
};

export default CopyReferral;
